// Front-side port of the coefficient rules (api/utils/validateCoefficientRanges.js).
// The back replays the exact same rules — the front can be bypassed and these values
// end up as billing rates, so the server check stays authoritative — but running them
// here lets "Confirmer" refuse to open the modal before any round trip.
//
// Returns the COMPLETE list of errors, not the first one: the card requires
// every faulty row to turn red.

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

// A range is either entirely empty — not configured, skipped — or entirely
// filled. Half-filled, it is an error.
const isEmpty = (range) => !isNumber(range.delegation) && !isNumber(range.management);
const isFilled = (range) => isNumber(range.delegation) && isNumber(range.management);

const validateCoefficientRanges = (ranges) => {
  const errors = [];
  const list = [...(ranges ?? [])];

  list.forEach((range, index) => {
    if (!isNumber(range?.percentage)) {
      errors.push({
        index,
        field: 'percentage',
        rule: 'R2',
        message: 'La tranche doit porter un montant valide.',
      });
      return;
    }

    // The montant is stored as a percentage above the SMIC, so a negative one
    // opens the bracket below the legal minimum: nothing could ever be billed
    // there. The SMIC rises while stored grids do not, which is how a company
    // nobody has touched ends up under it.
    if (range.percentage < 0) {
      errors.push({
        index,
        field: 'percentage',
        rule: 'R6',
        message: 'Le montant de la tranche ne peut pas être inférieur au SMIC.',
      });
    }
  });

  const sorted = list
    .map((range, index) => ({ ...range, index }))
    .filter((range) => isNumber(range.percentage))
    .sort((a, b) => a.percentage - b.percentage);

  sorted.forEach((range, position) => {
    const previous = position > 0 ? sorted[position - 1] : null;

    // R2 depends only on percentage, not on fill state, so it must run before
    // the fill-state checks below: a half-filled range's early return would
    // otherwise skip it and let two ranges keep the same montant unflagged.
    if (previous && range.percentage === previous.percentage) {
      [previous, range].forEach((target) =>
        errors.push({
          index: target.index,
          field: 'percentage',
          rule: 'R2',
          message: 'Deux tranches ne peuvent pas avoir le même montant.',
        })
      );
    }

    if (!isEmpty(range) && !isFilled(range)) {
      errors.push({
        index: range.index,
        field: isNumber(range.delegation) ? 'management' : 'delegation',
        rule: 'R1',
        message:
          'Renseignez les deux coefficients de la tranche, ou laissez-les tous les deux vides.',
      });
      return;
    }

    // Equality is a legitimate setup (a company billing management at the same
    // rate as delegation), so only a strictly higher gestion is refused.
    if (isFilled(range) && range.management > range.delegation) {
      errors.push({
        index: range.index,
        field: 'delegation',
        rule: 'R5',
        message: `Le coefficient de gestion (${range.management}) est supérieur au coefficient de délégation (${range.delegation}). Corrigez la valeur avant de confirmer.`,
      });
      errors.push({
        index: range.index,
        field: 'management',
        rule: 'R5',
        message: `Le coefficient de gestion (${range.management}) est supérieur au coefficient de délégation (${range.delegation}). Corrigez la valeur avant de confirmer.`,
      });
    }

    if (!previous || !isFilled(range) || !isFilled(previous)) return;

    // A plateau — the same coefficient on two consecutive ranges — is a
    // legitimate grid shape: the sector grid holds gestion at 2.26 across
    // 20,62–22,47 and 22,48–24,50, and CHR opens on its second range's pair.
    // Only a decrease is refused. Kept identical to the server rule in
    // api/utils/validateCoefficientRanges.js — the two are replayed on the
    // same values and must never disagree.
    if (range.delegation < previous.delegation) {
      [previous, range].forEach((target) =>
        errors.push({
          index: target.index,
          field: 'delegation',
          rule: 'R3',
          message:
            "Le coefficient de délégation ne peut pas diminuer d'une tranche à la suivante.",
        })
      );
    }

    if (range.management < previous.management) {
      [previous, range].forEach((target) =>
        errors.push({
          index: target.index,
          field: 'management',
          rule: 'R4',
          message:
            "Le coefficient de gestion ne peut pas diminuer d'une tranche à la suivante.",
        })
      );
    }
  });

  return errors;
};

export default validateCoefficientRanges;
